"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { citeLine, citationToSourceHit, renumberAnswer, tidyMarkdown, type SourceHit } from "@/lib/present";

interface AllSource {
  document_id: number;
  source_id?: string | null;
  title?: string | null;
  authors?: string[] | null;
  year?: number | null;
  issue?: string | null;
  landing_url?: string | null;
  src_kind?: string | null;
  marker?: string | null;
  pincite?: string | null;
  page_start?: number | null;
}

interface AnswerProps {
  answer: string;
  sources: { hit: SourceHit }[];
  /** Full deterministic "Visa källor" list (one entry per citing document),
   *  rendered at the end of the right-hand sources column. */
  allSources?: AllSource[];
  /** Extra actions rendered centered below the answer (not the sources). */
  footer?: React.ReactNode;
}

/**
 * Presents the /ask answer the way the old app did:
 * - [N]-markers moved after punctuation, collapsed and renumbered
 * - body via react-markdown, endnote markers as superscript links
 * - a "Källor" list in a right-hand column next to the answer
 * - the full source list ("Samtliga källor") at the end of that column
 */
export default function AnswerView({ answer, sources, allSources, footer }: AnswerProps) {
  const { text, endnotes } = renumberAnswer(tidyMarkdown(answer), sources.length);

  // Turn sequential [N] markers into superscript anchor links.
  const body = text.replace(/\[(\d{1,3})\]/g, (m, nStr) => {
    const n = parseInt(nStr, 10);
    return n >= 1 && n <= endnotes.length ? `[${n}](#endnote-${n})` : m;
  });

  const hasEndnotes = endnotes.length > 0 && sources.length > 0;
  const hasAllSources = !!allSources && allSources.length > 0;

  return (
    <div className="twocol">
      <main>
        <div className="card answer">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
        {footer && <div className="answer-footer">{footer}</div>}
      </main>

      {(hasEndnotes || hasAllSources) && (
        <aside className="endnotes" aria-label="Källor">
          {hasEndnotes && (
            <>
              <h3>Källor</h3>
              <ol>
                {endnotes.map((origIds, idx) => {
                  const n = idx + 1;
                  const items = origIds.map((i) => sources[i - 1]).filter(Boolean);
                  if (!items.length) return null;
                  return (
                    <li key={n} id={`endnote-${n}`} value={n}>
                      {items.map((ctx, si) => {
                        const { html, url } = citeLine(ctx.hit);
                        const sep = si > 0 ? "; " : "";
                        return (
                          <span key={si} className="lit-cite">
                            {sep}
                            <span
                              dangerouslySetInnerHTML={{
                                __html: url ? `<a href="${url}" target="_blank" rel="noreferrer">${html}</a>` : html,
                              }}
                            />
                          </span>
                        );
                      })}
                      .
                    </li>
                  );
                })}
              </ol>
            </>
          )}
          {hasAllSources && (
            <>
              <h3>Samtliga källor</h3>
              <ul className="all-sources">
                {allSources!.map((c) => {
                  const { html, url } = citeLine(citationToSourceHit(c));
                  return (
                    <li key={c.document_id} className="lit-cite">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: url ? `<a href="${url}" target="_blank" rel="noreferrer">${html}</a>` : html,
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </aside>
      )}
    </div>
  );
}